import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Alert,
  FormHelperText
} from '@mui/material';
import { useFaturamento } from '../../../use-cases/faturamento/useFaturamento';
import { useAppSelector, useAppDispatch } from '../../hooks/store-hooks';
import { setCreateClienteFormFaturamentoState, setEditClienteFormFaturamentoState } from '../../../application/states/clientes';
import { useFaturamentoRedux } from '../../../application/states/faturamento/useFaturamentoRedux';
import { INotaFiscalConfig } from '../../../application/models';

interface NotaFiscalTabProps {
  IdCliente: number;
  isEditMode?: boolean;
  isEditing?: boolean;
}

export interface NotaFiscalTabRef {
  getNotaFiscalData: () => INotaFiscalConfig | null;
}

// Componente para painel de tab
function CustomTabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nota-fiscal-tabpanel-${index}`}
      aria-labelledby={`nota-fiscal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `nota-fiscal-tab-${index}`,
    'aria-controls': `nota-fiscal-tabpanel-${index}`,
  };
}

export const NotaFiscalTab = forwardRef<NotaFiscalTabRef, NotaFiscalTabProps>(({ IdCliente, isEditMode = false, isEditing = false }, ref) => {
  // State
  const [ativo, setAtivo] = useState(false);
  const [diaVencimento, setDiaVencimento] = useState(15);
  const [simplesNacional, setSimplesNacional] = useState(false);
  const [nome, setNome] = useState('');
  const [descricaoPersonalizada, setDescricaoPersonalizada] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [dadosIniciaisCarregados, setDadosIniciaisCarregados] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);

  const dispatch = useAppDispatch();
  const createClienteForm = useAppSelector((state) => state.clientes.createClienteForm);
  const editClienteForm = useAppSelector((state) => state.clientes.editClienteForm);

  // Use Cases
  const {
    obterConfiguracaoPorCliente,
    error,
    getRawNotaFiscalData: getRawNotaFiscalDataService
  } = useFaturamento();

  // Redux para faturamento
  const {
    modelos,
    subtitulos: subtitulosRedux,
    carregarModelos: carregarModelosRedux,
    carregarSubtitulos,
    loading: loadingRedux
  } = useFaturamentoRedux();

  const [isLoadingData, setIsLoadingData] = useState(false);
  
  const subtitulos = subtitulosRedux?.subtitles || [];

  const diasVencimento = Array.from({ length: 28 }, (_, i) => i + 1);

  const normalizarAtivo = (ativo: any): boolean => {
  const valor = Array.isArray(ativo) ? ativo[0] : ativo;
  return Boolean(valor);
};

  const getRawNotaFiscalData = useCallback((): INotaFiscalConfig => {
    return getRawNotaFiscalDataService(
      ativo,
      diaVencimento,
      simplesNacional,
      nome,
      descricaoPersonalizada,
      modelos
    );
  }, [ativo, diaVencimento, simplesNacional, nome, descricaoPersonalizada, modelos, getRawNotaFiscalDataService]);

  const preencherCampos = (faturamentoData: any, ativoValue: boolean) => {
    setHasConfig(true);
    const clienteInativo = isEditMode && editClienteForm?.fields?.Ativo === false;
    const finalAtivo = clienteInativo ? true : ativoValue;
    setAtivo(finalAtivo);

    setDiaVencimento(faturamentoData.DiaVencimento || 15);
    setSimplesNacional(faturamentoData.SimplesNacional || false);
    setDescricaoPersonalizada(faturamentoData.NotaFiscalModeloDescricaoPersonalizado || '');

    if (faturamentoData.Nome) {
      setNome(faturamentoData.Nome);
    }
  };

  useEffect(() => {
    const faturamentoRedux = isEditMode
      ? editClienteForm?.fields?.faturamento
      : createClienteForm?.fields?.faturamento;

    if (faturamentoRedux) {
      const ativoValue = normalizarAtivo(faturamentoRedux.Ativo);
      preencherCampos(faturamentoRedux, ativoValue);

      setDadosIniciaisCarregados(true);
      return; 
    }

    if (IdCliente && !isNaN(IdCliente) && IdCliente > 0 && isEditMode && !dadosIniciaisCarregados) {
      const carregarDadosBackend = async () => {
        if (isLoadingData) return;

        try {
          setIsLoadingData(true);

          const configData = await obterConfiguracaoPorCliente({ IdCliente });

          if (configData) {
            setHasConfig(true);
            const ativoValue = normalizarAtivo(configData.Ativo);
            const diaVencimentoValue = Array.isArray(configData.DiaVencimento)
              ? configData.DiaVencimento[0]
              : configData.DiaVencimento;
            const simplesNacionalValue = Array.isArray(configData.SimplesNacional)
              ? configData.SimplesNacional[0]
              : configData.SimplesNacional;

            const clienteInativo = isEditMode && editClienteForm?.fields?.Ativo === false;
            const finalAtivo = clienteInativo ? true : ativoValue;
            setAtivo(finalAtivo);

            setDiaVencimento(diaVencimentoValue);
            setSimplesNacional(simplesNacionalValue);
            setDescricaoPersonalizada(configData.NotaFiscalModeloDescricaoPersonalizado || '');

            if ((configData as any).Nome) {
              setNome((configData as any).Nome);
            }
            else if (configData.IdNotaFiscalModeloDescricao && modelos.length > 0) {
              const modeloEncontrado = modelos.find(
                m => m.IdNotaFiscalModeloDescricao === configData.IdNotaFiscalModeloDescricao
              );
              if (modeloEncontrado) {
                setNome(modeloEncontrado.Nome);
              }
            }
          } else {
            // Cliente não possui configuração de faturamento - definir valores padrão
            setHasConfig(false);
            setAtivo(false);
            setDiaVencimento(15);
            setSimplesNacional(false);
            setDescricaoPersonalizada('');
            setNome('');
          }
        } finally {
          setIsLoadingData(false);
        }
      };

      carregarDadosBackend();

      setDadosIniciaisCarregados(true);
    }
  }, [
    isEditMode,
    editClienteForm?.fields?.faturamento,
    createClienteForm?.fields?.faturamento,
    editClienteForm?.fields?.Ativo,
    IdCliente,
    dadosIniciaisCarregados,
    isLoadingData,
    isEditing,
    modelos,
    obterConfiguracaoPorCliente 
  ]);

  // Forçar ativo como true se o cliente aberto para edição estiver inativo
  useEffect(() => {
    if (isEditMode && editClienteForm?.fields?.Ativo === false && dadosIniciaisCarregados && hasConfig) {
      setAtivo(true);
    }
  }, [editClienteForm?.fields?.Ativo, isEditMode, dadosIniciaisCarregados, hasConfig]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const dadosNotaFiscal = getRawNotaFiscalData();
      
      // Se inativo, enviar null ao invés de objeto
      const faturamentoConvertido = dadosNotaFiscal.Ativo ? {
        IdFaturamentoConfiguracaoNotaFiscal: 0,
        IdCliente: IdCliente,
        IdNotaFiscalModeloDescricao: nome && modelos.length > 0 ? modelos.find(m => m.Nome === nome)?.IdNotaFiscalModeloDescricao : undefined,
        NotaFiscalModeloDescricaoPersonalizado: dadosNotaFiscal.NotaFiscalModeloDescricaoPersonalizado,
        Ativo: dadosNotaFiscal.Ativo,
        DiaVencimento: dadosNotaFiscal.DiaVencimento,
        Inclusao: new Date().toISOString(),
        SimplesNacional: dadosNotaFiscal.SimplesNacional,
        Nome: dadosNotaFiscal.Nome
      } : null;

      if (isEditMode) {
        dispatch(setEditClienteFormFaturamentoState({ faturamento: faturamentoConvertido }));
      } else {
        dispatch(setCreateClienteFormFaturamentoState({ faturamento: faturamentoConvertido }));
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    ativo, diaVencimento, simplesNacional, nome, descricaoPersonalizada,
    isEditMode, dispatch, IdCliente, modelos,getRawNotaFiscalData
  ]);

  useEffect(() => {
    const carregarDados = async () => {
      if (isLoadingData || loadingRedux) return;

      try {
        setIsLoadingData(true);
        await Promise.all([
          carregarModelosRedux(),
          carregarSubtitulos()
        ]);
      } catch (error) {
        throw error;
      } finally {
        setIsLoadingData(false);
      }
    };

    if (modelos.length === 0 || subtitulos.length === 0) {
      carregarDados();
    }
  }, [carregarModelosRedux, carregarSubtitulos, isLoadingData, loadingRedux, modelos.length, subtitulos.length]);

  useEffect(() => {
    
    if (
      modelos.length > 0 &&
      ativo &&
      isEditMode &&
      editClienteForm?.fields?.faturamento &&
      editClienteForm.fields.faturamento.IdNotaFiscalModeloDescricao
    ) {
      const faturamentoRedux = editClienteForm.fields.faturamento;
      const modeloEncontrado = modelos.find(
        m => m.IdNotaFiscalModeloDescricao === faturamentoRedux.IdNotaFiscalModeloDescricao
      );
      if (modeloEncontrado) {
        if (!nome) {
          setNome(modeloEncontrado.Nome);
        }
        if (!descricaoPersonalizada) {
          setDescricaoPersonalizada(modeloEncontrado.Descricao);
        }
      }
    }
  }, [modelos, nome, ativo, isEditMode, editClienteForm?.fields?.faturamento, descricaoPersonalizada]);

  useEffect(() => {

    if (
      modelos.length > 0 &&
      nome &&
      ativo &&
      !descricaoPersonalizada.trim()
    ) {
      const modeloEncontrado = modelos.find(m => m.Nome === nome);
      if (modeloEncontrado) {
        setDescricaoPersonalizada(modeloEncontrado.Descricao);
      }
    }
  }, [modelos, nome, ativo, descricaoPersonalizada]);

  const getNotaFiscalData = () => {

    const newErrors: { [key: string]: string } = {};

    if (ativo) {
      if (!nome) {
        newErrors.nome = 'Modelo da Descrição é obrigatório';
      }

      if (!descricaoPersonalizada.trim()) {
        newErrors.descricaoPersonalizada = 'Descrição é obrigatória';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return null;
      }
    }

    return getRawNotaFiscalData();
  };

  

  useImperativeHandle(ref, () => ({
    getNotaFiscalData,
    getRawNotaFiscalData 
  }));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const clearErrors = () => {
    setErrors({});
  };

  const formatarDicionario = (variaveis: string[]) => {
    return variaveis.map((variavel, index) => {
      const limpa = variavel.trim();
      if (index === variaveis.length - 1) {
        return limpa;
      }
      return `${limpa}       `; 
    }).join('          '); 
  };
  
  if (isLoadingData || loadingRedux) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        color="primary"
        fontWeight="bold"
        textTransform={"uppercase"}
        textAlign={'center'}
        width={'100%'}
        my={2}
      >
        Configuração de Nota Fiscal
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => { }}>
            {error}
          </Alert>
        </Box>
      )}

      <Card variant='outlined'>
        <CardContent>
          {/* Switch Ativo */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={ativo}
                  onChange={(e) => {
                    setAtivo(e.target.checked);
                    clearErrors();
                  }}
                  color="primary"
                />
              }
              label={
                <Typography>
                  Ativo <span style={{ color: 'red' }}>*</span>
                </Typography>
              }
            />
          </Box>

          {ativo && (
            <>
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="configuração nota fiscal">
                  <Tab label="Configurações Básicas" {...a11yProps(0)} />
                  <Tab label="Modelos Disponíveis" {...a11yProps(1)} />
                </Tabs>
              </Box>

              {/* Tab 1: Configurações Básicas */}
              <CustomTabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.diaVencimento}>
                      <InputLabel>Dia de Vencimento <span style={{ color: 'red' }}>*</span></InputLabel>
                      <Select
                        value={diaVencimento}
                        label="Dia de Vencimento *"
                        onChange={(e) => {
                          setDiaVencimento(Number(e.target.value));
                          clearErrors();
                        }}
                      >
                        {diasVencimento.map((dia) => (
                          <MenuItem key={dia} value={dia}>
                            {dia}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.diaVencimento && (
                        <FormHelperText>{errors.diaVencimento}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={simplesNacional}
                          onChange={(e) => {
                            setSimplesNacional(e.target.checked);
                            clearErrors();
                          }}
                          color="secondary"
                        />
                      }
                      label={
                        <Typography>
                          Optante Simples Nacional <span style={{ color: 'red' }}>*</span>
                        </Typography>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!errors.nome}>
                      <InputLabel>Modelo da Descrição <span style={{ color: 'red' }}>*</span></InputLabel>
                      <Select
                        value={nome}
                        label="Modelo da Descrição *"
                        onChange={(e) => {
                          setNome(e.target.value);
                          clearErrors();

                          // Auto-preencher a descrição personalizada com o modelo selecionado
                          const modeloSelecionado = modelos.find(m => m.Nome === e.target.value);
                          if (modeloSelecionado) {
                            setDescricaoPersonalizada(modeloSelecionado.Descricao);
                          }
                        }}
                      >
                        {modelos.map((modelo) => (
                          <MenuItem key={modelo.IdNotaFiscalModeloDescricao} value={modelo.Nome}>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {modelo.Nome}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{
                                display: 'block',
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {modelo.Descricao.substring(0, 100)}...
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.nome && (
                        <FormHelperText>{errors.nome}</FormHelperText>
                      )}
                    </FormControl>
                    {/* Dicionário de Variáveis */}
                    {subtitulos.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: 1, borderColor: 'warning.300' }}>
                        <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                          <strong>📋 Dicionário de Variáveis:</strong>
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: 'white',
                            p: 2,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '200px',
                            overflow: 'auto',
                            border: 1,
                            borderColor: 'warning.200',
                            lineHeight: 1.8,
                            wordBreak: 'break-word'
                          }}
                        >
                          {formatarDicionario(subtitulos)}
                        </Box>
                      </Box>
                    )}
                    {/* Mostrar descrição do modelo selecionado */}
                    {nome && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          <strong>Descrição do Modelo Selecionado:</strong>
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: 'white',
                            p: 2,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '150px',
                            overflow: 'auto',
                            border: 1,
                            borderColor: 'grey.200'
                          }}
                        >
                          {modelos.find(m => m.Nome === nome)?.Descricao || 'Descrição não disponível'}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CustomTabPanel>



              {/* Tab 3: Modelos Disponíveis */}
              <CustomTabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Modelos de Nota Fiscal Disponíveis
                    </Typography>

                    {modelos.length > 0 ? (
                      <Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Selecione um modelo para ver sua descrição completa:
                        </Typography>

                        {modelos.map((modelo) => (
                          <Card
                            key={modelo.IdNotaFiscalModeloDescricao}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'grey.50'
                              }
                            }}
                            onClick={() => {
                              setNome(modelo.Nome);
                              setDescricaoPersonalizada(modelo.Descricao);
                              setTabValue(0); // Vai para a tab de descrição personalizada
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                  {modelo.Nome}
                                </Typography>
                                <Chip
                                  label={modelo.Ativo ? 'Ativo' : 'Inativo'}
                                  color={modelo.Ativo ? 'success' : 'error'}
                                  size="small"
                                />
                              </Box>

                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Descrição:</strong>
                              </Typography>

                              <Box
                                sx={{
                                  bgcolor: 'grey.100',
                                  p: 2,
                                  borderRadius: 1,
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  whiteSpace: 'pre-wrap',
                                  maxHeight: '200px',
                                  overflow: 'auto'
                                }}
                              >
                                {modelo.Descricao}
                              </Box>

                              <Box display="flex" gap={1} mt={1}>
                                <Chip
                                  label={`ID: ${modelo.IdNotaFiscalModeloDescricao}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`Inclusão: ${new Date(modelo.Inclusao).toLocaleDateString('pt-BR')}`}
                                  size="small"
                                  variant="outlined"
                                />
                                {modelo.Atualizacao && (
                                  <Chip
                                    label={`Atualização: ${new Date(modelo.Atualizacao).toLocaleDateString('pt-BR')}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}

                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                          <Typography variant="body2" color="info.main">
                            💡 <strong>Dica:</strong> Clique em qualquer modelo acima para selecioná-lo e ver sua descrição na aba "Descrição Personalizada"
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nenhum modelo disponível
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CustomTabPanel>
            </>
          )}

          {/* Informações da Configuração */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Informações da Configuração
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {/* <Chip
              label={`Cliente ID: ${IdCliente}`}
              color="primary"
              variant="outlined"
            /> */}
            <Chip
              label={`Status: ${ativo ? 'Ativo' : 'Inativo'}`}
              color={ativo ? 'success' : 'error'}
              variant="outlined"
            />
            {ativo && (
              <>
                <Chip
                  label={`Vencimento: ${diaVencimento}º dia`}
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  label={`Simples Nacional: ${simplesNacional ? 'Sim' : 'Não'}`}
                  color="warning"
                  variant="outlined"
                />
                {nome && (
                  <Chip
                    label={`Modelo: ${nome}`}
                    color="info"
                    variant="outlined"
                  />
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});
